import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useParams } from "react-router";
import { apiGet } from "../api";
import type { PortalConfig } from "../config";
import { dateTime, phone } from "../format";
import type { TicketDetail } from "../types";

/**
 * Check-in slips for the Zebra GK420D (4x6, printed through the print dialog; Joe, 2026-11-03):
 *   page 1: the check-in slip for the repair bag (old layout: date, ticket, shop and customer address,
 *           photo, QR of the ticket, description, wholesale job)
 *   then:   one work slip per watch: ticket and watch number, last name, the watch's photo, the rest blank.
 */
export function PrintSlips({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const { ticketNo = "" } = useParams();
  const [d, setD] = useState<TicketDetail | null>(null);
  const [qr, setQr] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.user) return;
    apiGet<TicketDetail>(config, auth.user.id_token ?? "", `/tickets/${encodeURIComponent(ticketNo)}`)
      .then(async (t) => { setQr(await QRCode.toDataURL(t.ticket.id, { width: 220, margin: 1, errorCorrectionLevel: "H" })); setD(t); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [auth.user, config, ticketNo]);

  // Print once every picture has loaded (or failed), as the old slip did.
  useEffect(() => {
    if (!d) return;
    let done = false;
    const go = () => { if (!done) { done = true; window.print(); } };
    const imgs = Array.from(document.querySelectorAll<HTMLImageElement>(".slip img"));
    Promise.all(imgs.map((i) => (i.complete ? Promise.resolve() : new Promise<void>((r) => { i.onload = () => r(); i.onerror = () => r(); }))))
      .then(() => window.setTimeout(go, 200));
    const t = window.setTimeout(go, 6000); // don't wait forever for a slow photo
    return () => window.clearTimeout(t);
  }, [d]);

  if (error) return <p className="no-print error">{error} <Link to={`/tickets/${ticketNo}`}>Back to the ticket</Link></p>;
  if (!d) return <p className="no-print muted">Getting the slips ready…</p>;

  const t = d.ticket;
  const c = d.customer;
  const name = [c?.firstName, c?.lastName].filter(Boolean).join(" ");
  const photoOf = (id: string | null) => d.photos.find((p) => p.id === id)?.url ?? null;
  const firstPhoto = photoOf(d.watches.find((w) => w.photoId)?.photoId ?? null) ?? d.photos[0]?.url ?? null;
  const printedAt = dateTime(new Date().toISOString());

  return (
    <div className="slips">
      <style>{`
        @page slip { size: 4in 6in; margin: 0; }
        .slip { page: slip; width: 4in; height: 6in; padding: 0.15in; box-sizing: border-box; overflow: hidden; break-after: page;
          font: 9pt Arial, sans-serif; color: #000; background: #fff; display: flex; flex-direction: column; gap: 0.07in; }
        .slip .row { display: flex; justify-content: space-between; gap: 0.1in; }
        .slip .row > :last-child { text-align: right; }
        .slip p { margin: 0; }
        .slip h2 { font-size: 13pt; margin: 0.04in 0; }
        .slip .big { font-size: 26pt; font-weight: bold; line-height: 1; }
        .slip .pic { display: grid; grid-template-columns: 3fr 1fr; gap: 0.08in; align-items: start; }
        .slip .pic img.photo { width: 100%; max-height: 2.2in; object-fit: contain; }
        .slip .pic img.qr { width: 100%; }
        .slip .work-photo { width: 100%; max-height: 2.4in; object-fit: contain; }
        .slip .lines { flex: 1; display: flex; flex-direction: column; justify-content: space-evenly; }
        .slip .lines span { border-bottom: 1px solid #000; height: 0.3in; }
        @media screen { .slips { background: #ddd; padding: 1rem; display: flex; flex-wrap: wrap; gap: 1rem; } .slip { box-shadow: 0 2px 8px rgb(0 0 0 / 0.3); } }
      `}</style>
      <div className="no-print print-toolbar" style={{ width: "100%" }}>
        <Link to={`/tickets/${t.ticketNo}`}>← Back to ticket #{t.ticketNo}</Link>
        <button type="button" className="primary" onClick={() => window.print()}>Print {1 + d.watches.length} slip{d.watches.length ? "s" : ""}</button>
        <span className="muted">Choose the Zebra printer, paper 4×6.</span>
      </div>

      <section className="slip">
        <div className="row"><span>{printedAt}</span><strong>#{t.ticketNo}</strong></div>
        <div className="row">
          <p>WRP Service Center<br />17100 W Bluemound Rd Ste 203<br />Brookfield WI 53005</p>
          <p>{name}<br />{[t.street, t.street2].filter(Boolean).join(" ")}<br />{[t.city, t.state, t.zip].filter(Boolean).join(" ")}<br />
            {c?.email}{c?.phone ? ` | ${phone(c.phone)}` : ""}</p>
        </div>
        <h2>{name} - #{t.ticketNo}</h2>
        <div className="pic">
          {firstPhoto ? <img className="photo" src={firstPhoto} alt="" /> : <span />}
          <img className="qr" src={qr} alt="" />
        </div>
        <p><strong>{d.watches.length || t.productCount || 0} watch{(d.watches.length || t.productCount) === 1 ? "" : "es"}</strong>
          {d.watches.length > 0 && <>: {d.watches.map((w) => `${t.ticketNo}-${w.position} ${[w.brand, w.model].filter(Boolean).join(" ") || "watch"}`).join("; ")}</>}</p>
        {t.description && <p>{t.description}</p>}
        {(t.wholesaleJobNumber || t.wholesaleJobName) && <p>{[t.wholesaleJobNumber, t.wholesaleJobName].filter(Boolean).join(" ")}</p>}
      </section>

      {d.watches.map((w) => {
        const photo = photoOf(w.photoId);
        return (
          <section className="slip" key={w.id}>
            <div className="row"><span className="big">{t.ticketNo}-{w.position}</span><strong className="big">{c?.lastName ?? ""}</strong></div>
            <p>{[w.brand, w.model].filter(Boolean).join(" ")}</p>
            {photo ? <img className="work-photo" src={photo} alt="" /> : <p className="muted">(no photo)</p>}
            <div className="lines"><span /><span /><span /><span /><span /></div>
          </section>
        );
      })}
    </div>
  );
}
